import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut, Moon, Sun, Sparkles, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { COMPANY_INFO, SERVICES } from '../utils/constants';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [linkPositions, setLinkPositions] = useState([]);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [crackers, setCrackers] = useState([]);
  const [flowers, setFlowers] = useState([]);
  const [shapeLines, setShapeLines] = useState([]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  const allServices = [
    { name: 'Software Development', path: '/services/software-development' },
    { name: 'Cloud Computing', path: '/services/cloud-computing' },
    { name: 'Cyber Security', path: '/services/cyber-security' },
    { name: 'VLSI Design', path: '/services/vlsi-design' },
    { name: 'AI & ML Solutions', path: '/services/ai-ml-solutions' },
    { name: 'Data Analytics', path: '/services/data-analytics' },
    { name: 'VLSI Services', path: '/services/vlsi' },
    { name: 'Blockchain & Web3', path: '/services/blockchain' },
    { name: 'Game Development', path: '/services/game-development' },
    { name: 'AI Automation', path: '/services/ai-automation' },
    { name: 'Startup MVP', path: '/services/startup-mvp' }
  ];

  const services = allServices.length > 0 ? allServices : (SERVICES?.map(s => ({
    name: s.name,
    path: `/services/${s.id}`
  })) || []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'Forum', path: '/forum' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Careers', path: '/careers' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const getShapeEmoji = (shapeName) => {
    const emojis = {
      heart: '❤️',
      star: '⭐',
      circle: '⭕',
      diamond: '💎',
      butterfly: '🦋',
      crown: '👑'
    };
    return emojis[shapeName] || '✨';
  };

  const popularShapes = [
    {
      name: 'heart',
      positions: [
        { x: 65, y: 28 }, { x: 70, y: 24 }, { x: 75, y: 23 }, { x: 80, y: 24 }, { x: 85, y: 28 },
        { x: 88, y: 35 }, { x: 90, y: 43 }, { x: 90, y: 52 },
        { x: 88, y: 60 }, { x: 84, y: 67 }, { x: 79, y: 73 }, { x: 75, y: 78 },
        { x: 71, y: 73 }, { x: 66, y: 67 }, { x: 62, y: 60 },
        { x: 60, y: 52 }, { x: 60, y: 43 }, { x: 62, y: 35 }
      ]
    },
    {
      name: 'star',
      positions: [
        { x: 75, y: 20 },
        { x: 78, y: 38 }, { x: 95, y: 42 },
        { x: 82, y: 54 }, { x: 87, y: 72 },
        { x: 75, y: 62 }, { x: 63, y: 72 },
        { x: 68, y: 54 }, { x: 55, y: 42 },
        { x: 72, y: 38 }
      ]
    },
    {
      name: 'circle',
      positions: [
        { x: 75, y: 22 },
        { x: 85, y: 25 }, { x: 92, y: 32 }, { x: 96, y: 42 },
        { x: 96, y: 52 }, { x: 92, y: 62 }, { x: 85, y: 69 }, { x: 75, y: 72 },
        { x: 65, y: 69 }, { x: 58, y: 62 }, { x: 54, y: 52 },
        { x: 54, y: 42 }, { x: 58, y: 32 }, { x: 65, y: 25 }
      ]
    },
    {
      name: 'diamond',
      positions: [
        { x: 75, y: 20 },
        { x: 85, y: 30 }, { x: 92, y: 42 }, { x: 95, y: 52 },
        { x: 92, y: 62 }, { x: 85, y: 72 }, { x: 75, y: 80 },
        { x: 65, y: 72 }, { x: 58, y: 62 }, { x: 55, y: 52 },
        { x: 58, y: 42 }, { x: 65, y: 30 }
      ]
    },
    {
      name: 'butterfly',
      positions: [
        { x: 75, y: 28 }, { x: 75, y: 38 }, { x: 75, y: 48 }, { x: 75, y: 58 },
        { x: 62, y: 32 }, { x: 55, y: 38 }, { x: 52, y: 48 }, { x: 55, y: 58 }, { x: 62, y: 64 },
        { x: 88, y: 32 }, { x: 95, y: 38 }, { x: 98, y: 48 }, { x: 95, y: 58 }, { x: 88, y: 64 }
      ]
    },
    {
      name: 'crown',
      positions: [
        { x: 60, y: 42 }, { x: 65, y: 32 }, { x: 70, y: 42 },
        { x: 75, y: 25 },
        { x: 80, y: 42 }, { x: 85, y: 32 }, { x: 90, y: 42 },
        { x: 92, y: 55 }, { x: 90, y: 65 }, { x: 85, y: 70 },
        { x: 75, y: 72 },
        { x: 65, y: 70 }, { x: 60, y: 65 }, { x: 58, y: 55 }
      ]
    },
  ];

  const constrainShapePositions = (positions) => {
    return positions.map(pos => ({
      x: Math.min(Math.max(pos.x, 8), 92),
      y: Math.min(Math.max(pos.y, 8), 92)
    }));
  };

  useEffect(() => {
    document.documentElement.style.webkitTextSizeAdjust = '100%';
    document.body.style.touchAction = 'manipulation';
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newIsDesktop = window.innerWidth >= 768;
      setIsDesktop(newIsDesktop);
      if (!newIsDesktop && navbarExpanded) {
        setNavbarExpanded(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navbarExpanded]);

  useEffect(() => {
    if (navbarExpanded && isDesktop) {
      const totalItems = navLinks.length + 2;
      const randomShape = popularShapes[Math.floor(Math.random() * popularShapes.length)];
      console.log(`🎨 Forming shape: ${randomShape.name.toUpperCase()} ${getShapeEmoji(randomShape.name)}`);

      const shapePositions = [...randomShape.positions];

      while (shapePositions.length < totalItems) {
        const basePos = shapePositions[Math.floor(Math.random() * shapePositions.length)];
        shapePositions.push({
          x: basePos.x + (Math.random() - 0.5) * 15,
          y: basePos.y + (Math.random() - 0.5) * 15
        });
      }

      const shuffled = shapePositions.sort(() => Math.random() - 0.5);
      const finalPositions = constrainShapePositions(shuffled.slice(0, totalItems));

      setLinkPositions(finalPositions);
      setShapeLines(randomShape.positions);

      triggerCrackers();
    } else {
      setServicesOpen(false);
      setShapeLines([]);
      setLinkPositions([]);
    }
  }, [navbarExpanded, isDesktop]);

  const triggerCrackers = () => {
    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 10 : 20;
    const newCrackers = [];

    newCrackers.push(
      { id: Date.now() + 1, x: 8, y: 50, size: 'large', delay: 0 },
      { id: Date.now() + 2, x: 38, y: 50, size: 'large', delay: 150 },
      { id: Date.now() + 3, x: 23, y: 30, size: 'large', delay: 300 }
    );

    for (let i = 0; i < particleCount; i++) {
      newCrackers.push({
        id: Date.now() + i + 10,
        x: Math.random() * 50 + 50,
        y: Math.random() * 80 + 10,
        size: 'small',
        delay: Math.random() * 1000
      });
    }

    setCrackers(newCrackers);

    setTimeout(() => {
      triggerFlowers();
    }, 1500);

    setTimeout(() => {
      setCrackers([]);
    }, 4000);
  };

  const triggerFlowers = () => {
    const newFlowers = [];
    const flowerColors = ['#ff1493', '#ff69b4', '#ffd700', '#ff6347', '#9370db', '#ff4500', '#ffa500', '#ff00ff'];

    const logoXCenter = navbarExpanded ? 23 : 5;
    const logoWidth = navbarExpanded ? 50 : 12;

    const flowerCount = window.innerWidth < 768 ? 12 : 35;

    for (let i = 0; i < flowerCount; i++) {
      const xOffset = (Math.random() - 0.5) * logoWidth;
      newFlowers.push({
        id: Date.now() + i,
        x: logoXCenter + xOffset,
        y: -5,
        size: Math.random() * 15 + 10,
        color: flowerColors[Math.floor(Math.random() * flowerColors.length)],
        rotation: Math.random() * 360,
        delay: Math.random() * 2000,
        duration: Math.random() * 3000 + 4000,
        sway: Math.random() * 40 - 20
      });
    }

    setFlowers(newFlowers);

    setTimeout(() => {
      setFlowers([]);
    }, 8000);
  };

  useEffect(() => {
    let wheelDelta = 0;
    const EXPAND_THRESHOLD = 500;

    const handleWheel = (e) => {
      const currentY = window.scrollY;
      if (isDesktop && currentY === 0 && e.deltaY < 0 && !navbarExpanded) {
        wheelDelta += Math.abs(e.deltaY);

        if (wheelDelta >= EXPAND_THRESHOLD) {
          setNavbarExpanded(true);
          wheelDelta = 0;
        }
      } else {
        wheelDelta = 0;
      }
    };

    const handleScroll = () => {
      const currentY = window.scrollY;
      const scrollingDown = currentY > lastScrollY;

      setScrolled(currentY > 20);

      if (scrollingDown && navbarExpanded) {
        setNavbarExpanded(false);
      }

      setLastScrollY(currentY);
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, navbarExpanded, isDesktop]);

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white dark:bg-gray-900 shadow-xl shadow-blue-500/10'
          : 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800'
      } ${navbarExpanded && isDesktop ? 'h-[75vh]' : 'min-h-16 md:min-h-20'}`}
    >
      <style dangerouslySetInnerHTML={{__html: `
        .cracker-burst { pointer-events: none; }
        .rocket-trail {
          position: absolute;
          width: 8px;
          height: 50px;
          background: linear-gradient(to bottom, 
            rgba(255, 255, 255, 1),
            rgba(255, 215, 0, 0.9),
            rgba(255, 69, 0, 0.7),
            rgba(255, 0, 0, 0.5),
            transparent
          );
          border-radius: 4px;
          transform: translateX(-4px) translateY(-25px);
          animation: rocketTrail 0.6s ease-out forwards;
          box-shadow: 0 0 30px rgba(255, 215, 0, 0.9);
        }
        @keyframes rocketTrail {
          0% {
            transform: translateX(-4px) translateY(60px) scaleY(0);
            opacity: 0;
          }
          50% { opacity: 1; }
          100% {
            transform: translateX(-4px) translateY(-50px) scaleY(1);
            opacity: 0;
          }
        }
        .particle {
          position: absolute;
          border-radius: 50%;
          background: var(--color);
          animation: burst 2.5s ease-out forwards;
          animation-delay: var(--delay);
          box-shadow: 0 0 10px var(--color), 0 0 20px var(--color), 0 0 30px var(--color);
          opacity: 0;
        }
        .particle.large {
          width: 12px;
          height: 12px;
          animation: burstLarge 3s ease-out forwards;
          animation-delay: var(--delay);
        }
        .particle.small {
          width: 6px;
          height: 6px;
          animation: burstSmall 2.2s ease-out forwards;
          animation-delay: var(--delay);
        }
        .sparkle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: #fff;
          border-radius: 50%;
          animation: sparkleAnim 2.5s ease-out forwards;
          animation-delay: var(--sparkle-delay);
          box-shadow: 0 0 6px #fff, 0 0 12px #fff;
          opacity: 0;
        }
        @keyframes burst {
          0% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
            transform: translate(0, 0) rotate(var(--angle)) translateX(15px) scale(1);
          }
          50% { opacity: 1; }
          100% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(200px) scale(0.2);
            opacity: 0;
          }
        }
        @keyframes burstLarge {
          0% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(0) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
            transform: translate(0, 0) rotate(var(--angle)) translateX(20px) scale(1.3);
          }
          40% { opacity: 1; }
          100% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(300px) scale(0.15);
            opacity: 0;
          }
        }
        @keyframes sparkleAnim {
          0% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(0) scale(0);
            opacity: 0;
          }
          20% { opacity: 1; }
          50% { opacity: 0.9; }
          100% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(150px) scale(0);
            opacity: 0;
          }
        }
        @keyframes burstSmall {
          0% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(0) scale(0);
            opacity: 0;
          }
          15% {
            opacity: 1;
            transform: translate(0, 0) rotate(var(--angle)) translateX(12px) scale(1);
          }
          60% { opacity: 0.8; }
          100% {
            transform: translate(0, 0) rotate(var(--angle)) translateX(140px) scale(0);
            opacity: 0;
          }
        }
        
        .flower-petal {
          position: absolute;
          pointer-events: none;
          animation: flowerFall var(--duration) ease-in-out forwards;
          animation-delay: var(--delay);
          opacity: 0;
        }
        .flower-petal::before {
          content: '';
          position: absolute;
          width: var(--size);
          height: var(--size);
          background: radial-gradient(ellipse at center, var(--color) 0%, transparent 70%);
          border-radius: 50% 0 50% 0;
          transform: rotate(var(--rotation));
          box-shadow: 0 0 10px var(--color);
        }
        @keyframes flowerFall {
          0% {
            transform: translateY(-20px) translateX(0) rotate(0deg) scale(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
            transform: translateY(0) translateX(0) rotate(0deg) scale(1);
          }
          50% {
            transform: translateY(40vh) translateX(var(--sway)) rotate(360deg) scale(1);
            opacity: 0.9;
          }
          100% {
            transform: translateY(80vh) translateX(calc(var(--sway) * 1.5)) rotate(720deg) scale(0.5);
            opacity: 0;
          }
        }
        
        @keyframes drawLine {
          from {
            stroke-dashoffset: 1000;
            opacity: 0;
          }
          to {
            stroke-dashoffset: 0;
            opacity: 1;
          }
        }
        
        .animate-drawLine { animation: drawLine 1.5s ease-out forwards; }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideDown { animation: slideDown 0.3s ease-out; }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-spin-slow { animation: spin-slow 3s linear infinite; }

        @media (max-width: 640px) {
          .navbar-logo-img {
            width: clamp(2.5rem, 10vw, 4rem) !important;
            height: clamp(2.5rem, 10vw, 4rem) !important;
          }
          .navbar-company-name {
            font-size: clamp(1rem, 5vw, 1.4rem) !important;
            line-height: 1.2;
            white-space: normal;
            word-break: break-word;
          }
          .navbar-tagline {
            font-size: clamp(0.65rem, 3vw, 0.85rem) !important;
          }
          .navbar-logo-container {
            gap: 0.5rem;
          }
        }

        @media (max-width: 768px) {
          nav {
            -webkit-text-size-adjust: 100%;
            touch-action: manipulation;
          }
          .animate-slideDown { animation: slideDown 0.2s ease-out; }
          .animate-drawLine { animation: drawLine 1s ease-out forwards; }
          .flower-petal { animation-duration: calc(var(--duration) * 0.6) !important; }
          .rocket-trail, .particle.large { display: none; }
        }

        @supports (-webkit-touch-callout: none) {
          nav {
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            -webkit-tap-highlight-color: transparent;
          }
          button {
            -webkit-user-select: none;
            -webkit-tap-highlight-color: rgba(0,0,0,0.1);
          }
        }
      `}}/>

      {crackers.map((cracker) => (
        <div
          key={cracker.id}
          className="cracker-burst"
          style={{
            position: 'absolute',
            left: `${cracker.x}%`,
            top: `${cracker.y}%`,
            animationDelay: `${cracker.delay}ms`,
            zIndex: 100,
            display: (window.innerWidth < 768 && cracker.size === 'large') ? 'none' : 'block'
          }}
        >
          {cracker.size === 'large' ? (
            <>
              <div className="rocket-trail" />
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="particle large"
                  style={{
                    '--angle': `${i * 18}deg`,
                    '--color': ['#ff0844', '#ffb700', '#00ff88', '#00b4ff', '#ff00de', '#fff200', '#ff4500', '#00ffff'][Math.floor(Math.random() * 8)],
                    '--delay': `${i * 0.04}s`
                  }}
                />
              ))}
              {[...Array(16)].map((_, i) => (
                <div
                  key={`spark-${i}`}
                  className="sparkle"
                  style={{
                    '--angle': `${i * 22.5}deg`,
                    '--sparkle-delay': `${0.3 + i * 0.04}s`
                  }}
                />
              ))}
            </>
          ) : (
            <>
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="particle small"
                  style={{
                    '--angle': `${i * 36}deg`,
                    '--color': ['#ff6b6b', '#ffd93d', '#6bcf7f', '#4d96ff', '#ff6bdc', '#ffa500'][Math.floor(Math.random() * 6)],
                    '--delay': `${i * 0.03}s`
                  }}
                />
              ))}
            </>
          )}
        </div>
      ))}

      {flowers.map((flower) => (
        <div
          key={flower.id}
          className="flower-petal"
          style={{
            left: `${flower.x}%`,
            top: `${flower.y}%`,
            '--size': `${flower.size}px`,
            '--color': flower.color,
            '--rotation': `${flower.rotation}deg`,
            '--delay': `${flower.delay}ms`,
            '--duration': `${flower.duration}ms`,
            '--sway': `${flower.sway}px`,
            zIndex: 99
          }}
        />
      ))}

      {navbarExpanded && isDesktop && shapeLines.length > 0 && (
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none z-40"
          style={{ height: '75vh' }}
        >
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.7" />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#ec4899" stopOpacity="0.7" />
            </linearGradient>
          </defs>
          
          {shapeLines.map((pos, index) => {
            const nextPos = shapeLines[(index + 1) % shapeLines.length];
            return (
              <line
                key={`line-${index}`}
                x1={`${pos.x}%`}
                y1={`${pos.y}%`}
                x2={`${nextPos.x}%`}
                y2={`${nextPos.y}%`}
                stroke="url(#lineGradient)"
                strokeWidth="4"
                strokeDasharray="10 6"
                className="animate-drawLine"
                style={{
                  animationDelay: `${index * 0.05}s`
                }}
              />
            );
          })}
          
          {shapeLines.map((pos, index) => (
            <g key={`dot-${index}`}>
              <circle
                cx={`${pos.x}%`}
                cy={`${pos.y}%`}
                r="14"
                fill="url(#lineGradient)"
                opacity="0.4"
                className="animate-pulse"
              />
              <circle
                cx={`${pos.x}%`}
                cy={`${pos.y}%`}
                r="7"
                fill="url(#lineGradient)"
                className="animate-pulse"
                style={{
                  animationDelay: `${index * 0.1}s`
                }}
              />
            </g>
          ))}
        </svg>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`flex justify-between items-center transition-all duration-500 ${
            navbarExpanded && isDesktop ? 'h-[75vh]' : 'min-h-16 md:min-h-20'
          }`}
        >
          <Link 
            to="/" 
            className={`flex items-center group z-10 transition-all duration-500 navbar-logo-container ${
              navbarExpanded ? 'space-x-0' : 'space-x-3'
            }`}
          >
            <div className="relative">
              <img
                src="/logo/nevxoaulio-logo.png"
                alt="NEVXOAULIO Logo"
                className={`navbar-logo-img object-contain transition-all duration-500 ${
                  navbarExpanded && isDesktop ? 'w-96 h-96 md:w-[32rem] md:h-[32rem]' : navbarExpanded && !isDesktop ? 'w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96' : 'w-12 h-12'
                } group-hover:scale-110 group-hover:rotate-3`}
              />
              {navbarExpanded && isDesktop && (
                <div className="absolute inset-0 bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200 dark:from-blue-400 dark:via-purple-400 dark:to-pink-400 opacity-30 dark:opacity-20 blur-3xl animate-pulse -z-10"></div>
              )}
            </div>
            {!navbarExpanded && (
              <div>
                <div className="navbar-company-name font-bold text-xl bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {COMPANY_INFO.name}
                </div>
                <div className="navbar-tagline text-xs text-gray-600 dark:text-gray-400">
                  {COMPANY_INFO.tagline}
                </div>
              </div>
            )}
          </Link>

          <div
            className={`hidden md:flex transition-all duration-500 ${
              navbarExpanded && isDesktop
                ? 'fixed inset-0 w-full h-[75vh] pointer-events-none'
                : 'items-center space-x-6'
            }`}
          >
            {navLinks.map((link, index) => (
              <div 
                key={link.name} 
                className="relative group pointer-events-auto"
                style={
                  navbarExpanded && isDesktop && linkPositions[index]
                    ? {
                        position: 'absolute',
                        left: `${linkPositions[index].x}%`,
                        top: `${linkPositions[index].y}%`,
                        transform: 'translate(-50%, -50%)',
                        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }
                    : {}
                }
              >
                {link.hasDropdown ? (
                  <>
                    <button
                      onClick={() => navbarExpanded && setServicesOpen(!servicesOpen)}
                      onMouseEnter={() => !navbarExpanded && setServicesOpen(true)}
                      className={`font-semibold flex items-center space-x-1 transition-all duration-300 relative ${
                        navbarExpanded && isDesktop
                          ? 'text-lg px-6 py-3 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white rounded-xl shadow-2xl hover:shadow-blue-500/50' 
                          : 'text-base text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                      } ${location.pathname.startsWith('/services') ? 'text-blue-600 dark:text-blue-400' : ''}`}
                    >
                      {navbarExpanded && isDesktop && (
                        <Sparkles className="w-5 h-5 mr-1 animate-pulse" />
                      )}
                      <span>{link.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {servicesOpen && (
                      <div 
                        className={`${
                          navbarExpanded && isDesktop
                            ? 'absolute top-full left-1/2 -translate-x-1/2 mt-2' 
                            : 'absolute top-full left-0 mt-2'
                        } w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-2 border-gray-200 dark:border-gray-700 py-2 z-50 transition-all duration-200 max-h-[70vh] overflow-y-auto animate-slideDown`}
                        onMouseEnter={() => !navbarExpanded && setServicesOpen(true)}
                        onMouseLeave={() => !navbarExpanded && setServicesOpen(false)}
                      >
                        {services.map((service) => (
                          <Link
                            key={service.path}
                            to={service.path}
                            className={`block px-5 py-3 text-sm font-medium transition-all ${
                              location.pathname === service.path
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400'
                            }`}
                            onClick={() => setServicesOpen(false)}
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`font-semibold transition-all duration-300 relative ${
                      navbarExpanded && isDesktop
                        ? 'text-lg px-6 py-3 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white rounded-xl shadow-2xl block hover:shadow-blue-500/50' 
                        : 'text-base text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                    } ${location.pathname === link.path ? 'text-blue-600 dark:text-blue-400' : ''}`}
                  >
                    {navbarExpanded && isDesktop && location.pathname === link.path && (
                      <Sparkles className="w-5 h-5 inline mr-1 animate-pulse" />
                    )}
                    {link.name}
                  </Link>
                )}
              </div>
            ))}

            <button
              onClick={toggleTheme}
              className={`p-3 rounded-xl transition-all duration-300 pointer-events-auto ${
                navbarExpanded && isDesktop
                  ? 'bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white shadow-2xl hover:shadow-blue-500/50' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
              style={
                navbarExpanded && isDesktop && linkPositions[navLinks.length]
                  ? {
                      position: 'absolute',
                      left: `${linkPositions[navLinks.length].x}%`,
                      top: `${linkPositions[navLinks.length].y}%`,
                      transform: 'translate(-50%, -50%)',
                      transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    }
                  : {}
              }
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-6 h-6 text-yellow-400 animate-spin-slow" />
              ) : (
                <Moon className="w-6 h-6 text-gray-700" />
              )}
            </button>

            {user ? (
              <div
                className={`pointer-events-auto ${
                  navbarExpanded && isDesktop
                    ? 'bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-xl shadow-2xl px-3 py-2 flex items-center gap-2' 
                    : 'flex items-center space-x-3'
                }`}
                style={
                  navbarExpanded && isDesktop && linkPositions[navLinks.length + 1]
                    ? {
                        position: 'absolute',
                        left: `${linkPositions[navLinks.length + 1].x}%`,
                        top: `${linkPositions[navLinks.length + 1].y}%`,
                        transform: 'translate(-50%, -50%)',
                        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }
                    : {}
                }
              >
                <button
                  onClick={() =>
                    navigate(
                      user.role === 'admin'
                        ? '/admin/dashboard'
                        : '/client/dashboard'
                    )
                  }
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                    navbarExpanded && isDesktop
                      ? 'text-white hover:bg-white/20'
                      : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                  }`}
                >
                  <User className="w-5 h-5" />
                  <span className="font-semibold">{user.name}</span>
                </button>
                {(user.role === 'admin' || user.role === 'support_agent') && (
                  <Link
                    to="/admin/chat"
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-semibold ${
                      navbarExpanded && isDesktop
                        ? 'text-white hover:bg-white/20'
                        : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                    } ${location.pathname === '/admin/chat' ? 'text-blue-600' : ''}`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="hidden sm:inline">Chat</span>
                  </Link>
                )}
                <button
                  onClick={logout}
                  className={`p-2 rounded-lg transition-all ${
                    navbarExpanded && isDesktop
                      ? 'text-white hover:bg-white/20'
                      : 'text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400'
                  }`}
                  aria-label="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className={`px-8 py-3 font-bold rounded-xl transition-all duration-300 pointer-events-auto ${
                  navbarExpanded && isDesktop
                    ? 'bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white shadow-2xl hover:shadow-blue-500/50 transform hover:scale-110' 
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl hover:shadow-blue-500/50 transform hover:-translate-y-0.5'
                }`}
                style={
                  navbarExpanded && isDesktop && linkPositions[navLinks.length + 1]
                    ? {
                        position: 'absolute',
                        left: `${linkPositions[navLinks.length + 1].x}%`,
                        top: `${linkPositions[navLinks.length + 1].y}%`,
                        transform: 'translate(-50%, -50%)',
                        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      }
                    : {}
                }
              >
                Login
              </Link>
            )}
          </div>

          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-gray-700" />
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2.5 rounded-lg transition-all font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg hover:shadow-xl hover:scale-110 transform duration-300 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>

        </div>
      </div>
      {isMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t-2 border-gray-200 dark:border-gray-800 max-h-[80vh] overflow-y-auto overscroll-contain animate-slideDown">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <div key={link.name}>
                <Link
                  to={link.path}
                  className={`block font-semibold py-3 px-4 rounded-lg transition-all min-h-[44px] flex items-center ${
                    location.pathname === link.path
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => !link.hasDropdown && setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
                {link.hasDropdown && (
                  <div className="pl-4 mt-2 space-y-2 pb-2 border-l-2 border-blue-600 ml-4">
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className={`block text-sm py-2 px-3 rounded-lg transition-all ${
                          location.pathname === service.path
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-blue-600 dark:hover:text-blue-400'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        → {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {user ? (
              <>
                <button
                  onClick={() => {
                    navigate(user.role === 'admin' ? '/admin/dashboard' : '/client/dashboard');
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left font-semibold py-3 px-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all min-h-[44px]"
                >
                  <User className="w-4 h-4 inline mr-2" />
                  Dashboard
                </button>
                {(user.role === 'admin' || user.role === 'support_agent') && (
                  <Link
                    to="/admin/chat"
                    className="block w-full text-left font-semibold py-3 px-4 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-all min-h-[44px]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <MessageCircle className="w-4 h-4 inline mr-2" />
                    Live Chat
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left font-semibold py-3 px-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-all min-h-[44px]"
                >
                  <LogOut className="w-4 h-4 inline mr-2" />
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-bold text-center shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-purple-700 transition-all min-h-[44px] flex items-center justify-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
